import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client';
import { withRouter } from 'react-router';
import { useForm } from 'react-hook-form';
import moment from 'moment';

import { toDecimalPlaces } from 'utils/calculator';
import { customerShape, passengersShape, hotelShape, historyShape } from 'constants/propTypes';
import { Row } from 'components/shared/Layout';
import Button from 'components/ui/Button';
import { EVOUCHER_DATE_FORMAT, MAX_VOUCHER_EXPIRY_DATE, VOUCHER_LANGUAGES } from 'constants/payment';
import { voucherExpiryDateInvalid, getPassengersByUniqueEmails } from 'components/ui/Payments/Payments.utils';
import { ErrorMessage } from 'components/ui/Payments/SplitRefundMethods/CardRefund/CardRefund.styles';
import AlertBox from 'components/ui/AlertBox';
import { CurrencyInput } from 'components/ui/Inputs';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import PriceTable from './PriceTable';
import { PROCESS_HOTEL_VOUCHER_REFUND } from 'store/mutations';
import Modal from 'components/ui/Modal';

import { StyledLabel, Spacer } from './HotelCompensation.styles';
import EmailLanguage from 'components/ui/EmailLanguage';
import StyledReactSelect from 'components/ui/StyledReactSelect';
import TextInput from 'components/ui/TextInput';

const HotelCompensationVoucher = ({ history, reference, loading, hotel, passengers, customer }) => {
  const totalPrice = toDecimalPlaces(2, hotel.priceWithMargin);
  const currency = hotel.currency;
  const [processHotelVoucherRefund, { loading: processHotelVoucherRefundLoading }] = useMutation(PROCESS_HOTEL_VOUCHER_REFUND);

  const { register, handleSubmit, watch, setValue, formState } = useForm({
    mode: 'onChange',
    defaultValues: {
      compensationAmount: 0,
      voucherEmail: customer.email || '',
      voucherExpiry: MAX_VOUCHER_EXPIRY_DATE,
      voucherLanguage: ''
    }
  });

  const { errors } = formState;

  const { compensationAmount, voucherExpiry, voucherEmail, voucherLanguage } = watch();
  const allPassengers = getPassengersByUniqueEmails(customer, passengers);
  const passengerOptions = allPassengers.map(({ email }) => ({ label: email, value: email }));
  const refundAmount = toDecimalPlaces(2, Number(compensationAmount));
  const refundDisabled = !formState.isValid || loading || processHotelVoucherRefundLoading || totalPrice < refundAmount;

  const [refundError, setRefundError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleEmailLanguageChange = (languageObject) => {
    setValue('voucherLanguage', languageObject.value, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleEmailChange = (emailObject) => {
    setValue('voucherEmail', emailObject.value, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const onHandleCalendarChange = (event) => {
    setValue('voucherExpiry', event.target.value, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const redirectToExtrasPage = () => {
    history.push(`/manage/bookings/${reference}/extras`);
  };

  const onSubmitForm = async (form) => {
    const { voucherEmail: email, voucherExpiry: expiryDate, voucherLanguage } = form;

    const { firstName, lastName } = allPassengers.find((pax) => pax.email === voucherEmail);

    const variables = {
      bookingReference: reference,
      refundAmount,
      currency,
      email,
      firstName,
      lastName,
      language: voucherLanguage,
      expiryDate: moment(expiryDate, EVOUCHER_DATE_FORMAT).format('YYYY-MM-DD')
    };

    try {
      await processHotelVoucherRefund({ variables });
    } catch (err) {
      setRefundError(err.message);
      setIsOpen(true);
      return;
    }

    redirectToExtrasPage();
  };

  const onClose = () => {
    setIsOpen(false);
  };

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div>{refundError}</div>
        <Button data-cy="button-error-modal" type="button" onClick={redirectToExtrasPage}>
          OK
        </Button>
      </Modal>

      <h2>
        <FormattedMessage id="prrRefund.compensationAmount" />
      </h2>
      <Row>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Row>
            <CurrencyInput
              currency={currency}
              dataCy="compensation-amount"
              dataTestId="compensation-amount"
              isControlled={false}
              {...register('compensationAmount', { required: true, min: 0.01, max: totalPrice })}
            />
          </Row>
          <PriceTable currency={currency} totalPrice={totalPrice} refundAmount={refundAmount} />
          <div>
            <StyledLabel>
              <FormattedMessage id="refunds.evoucher.marketSendVoucher" />
            </StyledLabel>
            <EmailLanguage
              id="voucherLanguage"
              data-cy="create-voucher-language--dropdown"
              placeholder={<FormattedMessage id="payments.dropdownDefault" />}
              initialValue={voucherLanguage}
              width="300px"
              mt="8px"
              disabled={loading}
              {...register('voucherLanguage', {
                required: true,
                validate: (value) => VOUCHER_LANGUAGES.includes(value),
                onChange: handleEmailLanguageChange
              })}
              filterOption={({ value }) => VOUCHER_LANGUAGES.includes(value)}
            />
          </div>
          <div>
            <StyledLabel>
              <FormattedMessage id="refunds.evoucher.emailSendVoucher" />
            </StyledLabel>
            <StyledReactSelect
              id="voucherEmail"
              data-cy="create-voucher-email--dropdown"
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder={<FormattedMessage id="payments.dropdownDefault" />}
              value={passengerOptions.find((o) => o.value === voucherEmail)}
              options={passengerOptions}
              {...register('voucherEmail', { required: true, onChange: handleEmailChange })}
              width="300px"
              mt="8px"
            />
          </div>
          <div>
            <StyledLabel>
              <FormattedMessage id="refunds.evoucher.expiryDateCreate" />
            </StyledLabel>
            <TextInput
              id="voucherExpiry"
              data-cy="create-voucher-expiry"
              type="date"
              pattern="dd/mm/yyyy"
              value={moment(voucherExpiry).format(EVOUCHER_DATE_FORMAT)}
              min={moment().format(EVOUCHER_DATE_FORMAT)}
              max={moment(MAX_VOUCHER_EXPIRY_DATE).format(EVOUCHER_DATE_FORMAT)}
              {...register('voucherExpiry', {
                required: true,
                validate: (value) => !voucherExpiryDateInvalid(value),
                onChange: onHandleCalendarChange
              })}
              style={{ height: 32, width: 300, marginTop: 8 }}
            />
            {errors.voucherExpiry && (
              <Spacer>
                <AlertBox variant="ERROR">
                  <FormattedMessage id="refunds.evoucher.voucherExpiryDateError" />
                </AlertBox>
              </Spacer>
            )}
          </div>

          {totalPrice < refundAmount && (
            <ErrorMessage data-cy="voucher-max-total-amount-error-msg">
              <FormattedMessage id="hotelCompensation.maxCompensationError" /> <FormattedCurrency currency={currency} amount={totalPrice} />
            </ErrorMessage>
          )}

          <Button
            data-cy="submit-hotel-compensation"
            type="submit"
            disabled={refundDisabled}
            loading={loading || processHotelVoucherRefundLoading}
          >
            <FormattedMessage id="prrRefund.processRefundCta" />
          </Button>
        </form>
      </Row>
    </div>
  );
};

HotelCompensationVoucher.propTypes = {
  history: historyShape,
  onSubmit: PropTypes.func,
  passengers: passengersShape,
  customer: customerShape,
  loading: PropTypes.bool,
  hotel: hotelShape,
  reference: PropTypes.string
};

export default withRouter(HotelCompensationVoucher);
